// MATERIAL UI
import {
  Box,
  Button,
  FormHelperText,
  Select,
  styled,
  Typography,
  Avatar,
  TextField,
} from "@mui/material";

// TYPES
import { InputStyleType } from "../../../input/FormInput";

export const StyledWithdrawalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "500px",
  height: "auto",
  backgroundColor: "#fff",
  borderRadius: "10px",
  transform: "translate(-50%, -50%)",
  boxShadow: "24",
  display: "block",
  outline: "none",
  flexDirection: undefined, // Only set flexDirection for non-mobile
  [theme.breakpoints.down("md")]: {
    borderRadius: "0px",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    transform: "none",
    boxShadow: "none",
    flexDirection: "column",
  },
}));

export const StyledTitleContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  width: "100%",
  paddingRight: "28px",
  paddingLeft: "28px",
  borderRadius: "10px 10px 0px 0px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "60px",
  [theme.breakpoints.down("md")]: {
    borderRadius: "0px",
  },
}));

export const StyledMainContainer = styled(Box)(() => ({
  height: "100%",
  alignItems: "center",
  padding: "22px",
  flexGrow: 1,
  overflow: "auto",
}));

export const StyledModalTitle = styled(Typography)(() => ({
  fontSize: "23px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "28px ",
  letterSpacing: " -0.46px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  weight: "100%",
}));

export const StyledNextButton = styled(Button)(({ theme, disabled }) => ({
  backgroundColor: disabled
    ? theme.palette.action.disabledBackground
    : theme.palette.secondary.main,
  width: "100%",
  fontSize: "18px",
  height: "48px",
  textTransform: "capitalize",
  color: disabled ? theme.palette.action.disabled : "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  "&:hover": {
    backgroundColor: disabled
      ? theme.palette.action.disabledBackground
      : theme.palette.primary.main,
  },
}));

export const StyledSecurityNoticeTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "19px",
}));

export const StyledFormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  position: "relative",
}));

export const StyledStepHeaderTypography = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  color: theme.palette.secondary.main,
}));

export const StyledExplainerTextTypography = styled(Typography)(() => ({
  fontSize: "14px",
  lineHeight: "17px",
  padding: "12px 0",
  color: "#5C6469",
}));

export const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  marginLeft: "0px",
  display: "ruby-text",
  bottom: 20,
  alignItems: "center",
  gap: "8px",
  color: theme.palette.secondary.main,
  paddingBottom: "20px",
}));

export const StyledReviewTitle = styled(Typography)(() => ({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px" /* 121.429% */,
  color: "#5C6469",
  padding: "12px 0",
}));

export const StyledReviewDetails = styled(Typography)(() => ({
  fontFamily: "Söhne",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "19px" /* 121.429% */,
  color: "#002035",
}));

export const StyledSelectContainer = styled(Select)(() => ({
  position: "relative",
  fontFamily: "Roobert",
  fontSize: "16px",
  minWidth: 200,
  maxWidth: "100%",
  borderRadius: "10px",
  "&.Mui-disabled": {
    WebkitTextFillColor: "#E5E7E8 !important",
  },
  outline: "none",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "1px solid var(--Neutral-100, #E5E7E8)",
  },
  ".MuiSelect-select": {
    padding: "12px",
  },
  ".MuiSvgIcon-root ": {
    fill: "#000000",
    marginRight: "8px",
  },
}));

export const StyledAvatar = styled(Avatar)(() => ({
  height: "20px",
  width: "20px",
  marginRight: "10px",
}));

export const StyledSecondaryFormLabel = styled(Typography)(() => ({
  WebkitTextFillColor: "#D1D4D6",
  fontSize: "16px",
}));

export const StyledFormLabel = styled(Typography)(({ theme }) => ({
  marginBottom: "12px",
  fontSize: "14px",
  color: theme.palette.text.secondary,
}));

export const StyledInputContainer = styled(TextField)<{
  styletype?: InputStyleType;
  inputSx?: any;
}>(({ theme, disabled, styletype, inputSx }) => ({
  height: "43px",
  position: "relative",
  minWidth: 200,
  ".MuiInputBase-root": {
    borderRadius: "8px",
    height: "100%",
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px",
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  ...(styletype === InputStyleType.ERROR && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "red",
      },
      "&:hover fieldset": {
        borderColor: "red",
      },
      "&.Mui-focused fieldset": {
        borderColor: "red",
      },
    },
  }),
  ...(styletype === InputStyleType.FORM && {
    ".MuiInputBase-root": {
      fontSize: "14px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
    },
  }),
  ...(styletype === InputStyleType.FORM_ERROR && {
    ".MuiInputBase-root": {
      fontSize: "14px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": {
        borderColor: "red",
      },
      "&:hover fieldset": {
        borderColor: "red",
      },
      "&.Mui-focused fieldset": {
        borderColor: "red",
      },
    },
  }),
  ...inputSx,
}));

export const StyledMaxButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  right: 0,
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.palette.primary.main,
  fontSize: "14px",
  textTransform: "none",
  textDecoration: "underline",
}));
